import { DebouncedInput } from 'components/debounced-input';
import { ReactNode } from 'react';
import { CollapseAll, ExpandAll } from '@carbon/react/icons';
import { Button } from '@carbon/react';
import { useDataTableContext } from '../context';
import { ExportButton } from '../export-button';
import { ExportConfig } from '../types';
import { ColumnFilterToggle } from './column-filter-toggle';
import { ColumnVisibility } from './column-visibility';
import { FilterToggle } from './filter-toggle';
import { ResetCustomization } from './reset-customization';
import styles from './table-toolbar.module.scss';

// ─────────────────────────────────────
// Props
// ─────────────────────────────────────

interface TableToolbarProps {
  /** Placeholder text for global search input */
  searchPlaceholder?: string;
  /** Show the global search input */
  showGlobalFilter?: boolean;
  /** Show the filter panel toggle button */
  showFilterToggle?: boolean;
  /** Show the inline column filter toggle button */
  showColumnFilterToggle?: boolean;
  /** Show the column visibility menu */
  showColumnVisibility?: boolean;
  /** Show the reset button */
  showReset?: boolean;
  /** Show the export button */
  showExport?: boolean;
  /** Export configuration (optional, uses defaults if not provided) */
  exportConfig?: ExportConfig;
  /** Callback to collapse all expanded rows. When provided, shows the collapse button. */
  onCollapseAll?: () => void;
  /**
   * When true, the collapse-all button becomes an expand ⇄ collapse toggle
   * (icon swaps between ExpandAll/CollapseAll, expanding or collapsing every row
   * on the current page). Takes precedence over the plain `onCollapseAll` button.
   */
  expandAllToggle?: boolean;
  /**
   * Optional nodes rendered inside the right-hand action group, before the export and
   * column controls.
   *
   * The name is a historical misnomer and the placement is load-bearing for the two dozen
   * call sites that pass it, so neither is changed here. `fillLeftToolbarActions` opts into
   * the placement the name describes.
   */
  leftToolbarActions?: ReactNode[];
  /**
   * Render `leftToolbarActions` as a real left-hand group that grows into the free space,
   * instead of inside the right-pinned action group.
   *
   * Needed by any toolbar whose actions include a search field: `.actions` is
   * `margin-left: auto` and sized to its content, so a field in there cannot grow no matter
   * what flex it is given — it renders small and pinned to the far right. Opt-in rather than
   * the default because ~24 call sites pass short button groups that are correct where they
   * are, and moving them would be a visual change to all of them.
   */
  fillLeftToolbarActions?: boolean;
  /** Nodes before the search field, for a filter panel that lives outside the table. */
  leadingToolbarActions?: ReactNode[];
  /** Optional nodes rendered at the far right of the toolbar (e.g. a Save button) */
  rightToolbarActions?: ReactNode[];
}

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * Main toolbar component for the DataTable.
 * Contains global search, filter toggles, column visibility menu, export, and reset buttons.
 * All state is accessed via DataTableContext.
 * @param {TableToolbarProps} props - Component props
 * @param {string} [props.searchPlaceholder] - Placeholder for search input
 * @param {boolean} [props.showGlobalFilter] - Show global search input (default: true)
 * @param {boolean} [props.showFilterToggle] - Show filter panel toggle (default: true)
 * @param {boolean} [props.showColumnFilterToggle] - Show column filter toggle (default: false)
 * @param {boolean} [props.showColumnVisibility] - Show column visibility menu (default: true)
 * @param {boolean} [props.showReset] - Show reset button (default: true)
 * @param {boolean} [props.showExport] - Show export button (default: false)
 * @param {ExportConfig} [props.exportConfig] - Export configuration options
 * @return {JSX.Element} The toolbar element
 */
export function TableToolbar({
  searchPlaceholder = 'Search...',
  showGlobalFilter = true,
  showFilterToggle = true,
  showColumnFilterToggle = false,
  showColumnVisibility = true,
  showReset = true,
  showExport = false,
  exportConfig,
  onCollapseAll,
  expandAllToggle = false,
  leftToolbarActions,
  fillLeftToolbarActions = false,
  leadingToolbarActions,
  rightToolbarActions,
}: TableToolbarProps) {
  const { globalFilter, setGlobalFilter, table } = useDataTableContext();
  // Not getIsAllRowsExpanded/getCanSomeRowsExpand: they ignore getCanExpand and pagination.
  const expandable = expandAllToggle
    ? table.getRowModel().rows.filter((row) => row.getCanExpand())
    : [];
  const allRowsExpanded =
    expandable.length > 0 && expandable.every((row) => row.getIsExpanded());

  return (
    <div className={styles.toolbar}>
      {/* Filter Panel Toggle - LEFT of search */}
      {showFilterToggle && <FilterToggle />}
      {leadingToolbarActions}
      {/* Search Input */}
      {showGlobalFilter && (
        <div className={styles.searchArea}>
          <DebouncedInput
            value={globalFilter ?? ''}
            onChange={(value) => setGlobalFilter(String(value).trim())}
            placeholder={searchPlaceholder}
          />
        </div>
      )}
      {/* Left Toolbar Actions, where the name says — opted into, and able to grow. */}
      {fillLeftToolbarActions &&
        leftToolbarActions &&
        leftToolbarActions.length > 0 && (
          <div className={styles.leftActionsFill}>{leftToolbarActions}</div>
        )}
      {/* Action Buttons - RIGHT side */}
      <div className={styles.actions}>
        {/* Left Toolbar Actions, in their historical place inside the right-hand group. */}
        {!fillLeftToolbarActions &&
          leftToolbarActions &&
          leftToolbarActions.length > 0 && (
            <div className={styles.leftActions}>{leftToolbarActions}</div>
          )}
        {/* Export Button */}
        {showExport && <ExportButton config={exportConfig} />}

        {/* Column Filter Toggle */}
        {showColumnFilterToggle && <ColumnFilterToggle />}

        {/* Column Visibility */}
        {showColumnVisibility && <ColumnVisibility />}

        {/* Reset Button */}
        {showReset && <ResetCustomization />}

        {/* Expand/Collapse All — a toggle when opted in, else collapse-only */}
        {expandAllToggle ? (
          <Button
            kind="ghost"
            size="lg"
            hasIconOnly
            renderIcon={allRowsExpanded ? CollapseAll : ExpandAll}
            iconDescription={
              allRowsExpanded ? 'Collapse all rows' : 'Expand all rows'
            }
            tooltipPosition="bottom"
            tooltipAlignment="end"
            disabled={expandable.length === 0}
            onClick={() => table.toggleAllRowsExpanded(!allRowsExpanded)}
          />
        ) : (
          onCollapseAll && (
            <Button
              kind="ghost"
              size="lg"
              hasIconOnly
              renderIcon={CollapseAll}
              iconDescription="Collapse all rows"
              tooltipPosition="bottom"
              tooltipAlignment="end"
              onClick={onCollapseAll}
            />
          )
        )}
        {/* Right Toolbar Actions */}
        {rightToolbarActions}
      </div>
    </div>
  );
}
